<template>
  <div id="AdminTransactionsNew">
    <div class="container-fluid mt-5 mb-5 px-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h2>
          {{
            isEditMode
              ? `Edit Transaction #${transactionId}`
              : "New Transaction"
          }}
        </h2>
        <div class="d-flex align-items-center gap-3">
          <NewSaleLinksComponent v-if="offersDealLinks" current="spot" />
          <router-link :to="listRoute" class="btn btn-outline-secondary">
            <font-awesome-icon icon="arrow-left" /> Back to Transactions
          </router-link>
        </div>
      </div>

      <!-- Transaction Status Badge (Edit Mode Only) -->
      <div v-if="isEditMode && currentTransaction" class="mb-3">
        <span
          class="badge"
          :class="getStatusBadgeClass(currentTransaction.status)"
        >
          {{ currentTransaction.status }}
        </span>
      </div>

      <!-- Forwards v2: this transaction is a delivery of a deal, which owns its figures -->
      <ForwardsTransactionHeaderComponent
        v-if="forwardDelivery"
        :header="forwardDelivery"
      />

      <!-- Foldable: Buyer Details & Contracts -->
      <div class="card mb-3">
        <div
          class="card-header bg-light d-flex justify-content-between align-items-center"
          role="button"
          @click="detailsPanelOpen = !detailsPanelOpen"
        >
          <h5 class="mb-0">
            Buyer Details & Contracts<span
              v-if="counterpartyName"
              class="fw-normal text-muted ms-2"
              >· {{ counterpartyName }}
              <template v-if="counterpartyCountry">
                ({{ counterpartyCountry }})</template
              >
              <template v-if="counterparty_eecs_account_number">
                · {{ counterparty_eecs_account_number }}</template
              >
              <template v-if="transactionTotalMwh">
                · {{ transactionTotalMwh }} MWh</template
              >
              <template v-if="transactionLink">
                ·
                <a
                  :href="transactionLink"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click.stop
                  class="text-primary"
                  >{{ transactionLink }}</a
                ></template
              ></span
            >
          </h5>
          <font-awesome-icon
            :icon="detailsPanelOpen ? 'chevron-up' : 'chevron-down'"
          />
        </div>
        <div v-show="detailsPanelOpen" class="card-body">
          <div class="row">
            <div class="col-lg-6">
              <CounterpartyPicker
                ref="counterpartyPicker"
                v-model="counterpartyModel"
                :registrationAuthority="registrationAuthority"
                :disabled="isForwardDelivery"
                @select:company="handleCompanySelected"
              />
              <div class="mb-3">
                <label for="counterpartyCountry" class="form-label"
                  >Counterparty Country (billing)</label
                >
                <SearchableSelectComponent
                  :options="ALL_COUNTRIES"
                  v-model="counterpartyCountry"
                  placeholder="Select country..."
                  :disabled="isForwardDelivery"
                />
              </div>
              <div class="mb-3" v-if="registrationAuthority === 'RGP'">
                <label for="registryUnitPricePln" class="form-label"
                  >Unit Price (PLN/MWh)</label
                >
                <input
                  type="number"
                  class="form-control"
                  id="registryUnitPricePln"
                  v-model="registryUnitPricePln"
                  min="0.01"
                  step="0.01"
                  :class="{
                    'is-invalid': !isValidRegistryUnitPrice,
                  }"
                  @input="onRegistryUnitPriceInput"
                />
                <div class="invalid-feedback">
                  Must be a positive amount with at most 2 decimal places
                </div>
                <small class="form-text text-muted d-block">
                  Contract unit price in PLN — sent to TGE with the transfer.
                </small>
              </div>
              <div class="mb-3">
                <label for="counterpartyEmails" class="form-label"
                  >Counterparty Email Addresses (Optional)</label
                >
                <textarea
                  id="counterpartyEmails"
                  v-model="counterpartyEmails"
                  class="form-control"
                  rows="2"
                  :disabled="isForwardDelivery"
                  placeholder="accountant@buyer.com, finance@buyer.com"
                ></textarea>
                <div class="form-text">
                  Enter email addresses separated by commas for invoice
                  notifications.
                </div>
              </div>
              <div class="mb-3">
                <label for="transactionDate" class="form-label"
                  >Transaction Date</label
                >
                <input
                  type="date"
                  class="form-control"
                  id="transactionDate"
                  v-model="transactionDate"
                  :disabled="isForwardDelivery"
                />
              </div>
              <div class="mb-3">
                <label for="currency" class="form-label">Currency</label>
                <select
                  id="currency"
                  v-model="currency"
                  class="form-select"
                  data-testid="currency-select"
                  :disabled="isCurrencyFixed"
                >
                  <option
                    v-for="option in SUPPORTED_CURRENCIES"
                    :key="option"
                    :value="option"
                    :disabled="
                      option !== DEFAULT_CURRENCY &&
                      allocationHoldsLimitPriceRows
                    "
                  >
                    {{ option }}
                  </option>
                </select>
                <small class="form-text text-muted d-block">
                  The sell, buy and override prices, and every invoice line of
                  the deal, are in this currency.
                  <template v-if="isCurrencyFixed">
                    Fixed once the deal is a forward delivery, completed, or has
                    anything posted.
                  </template>
                  <span
                    v-else-if="allocationHoldsLimitPriceRows"
                    data-testid="limit-price-currency-note"
                    >Holds limit price portfolios, so it stays in EUR.</span
                  >
                </small>
              </div>
              <div class="row">
                <div class="col-md-4 mb-3">
                  <label for="unitPrice" class="form-label"
                    >Sell Price ({{ currency }}/MWh)</label
                  >
                  <input
                    type="number"
                    class="form-control"
                    id="unitPrice"
                    data-testid="unit-price-input"
                    v-model.number="unitPrice"
                    :class="{ 'is-invalid': hasMinPriceViolation }"
                    min="0"
                    step="0.0001"
                    :disabled="isForwardDelivery"
                  />
                  <div
                    v-if="hasMinPriceViolation"
                    class="alert alert-danger py-2 px-3 mt-2 mb-0 small"
                    data-testid="unit-price-error"
                  >
                    {{ MIN_PRICE_VIOLATION_MESSAGE }}
                  </div>
                  <div
                    v-if="excludesLimitPriceRows"
                    class="form-text text-warning"
                    data-testid="limit-price-note"
                  >
                    Limit price portfolios can't be sold in a non-EUR deal yet.
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <label for="buyPrice" class="form-label"
                    >Buy Price (producer, {{ currency }}/MWh)</label
                  >
                  <input
                    type="number"
                    class="form-control"
                    id="buyPrice"
                    data-testid="buy-price-input"
                    v-model.number="buyPrice"
                    min="0"
                    step="0.0001"
                    :class="{ 'is-invalid': !isValidBuyPrice }"
                    :disabled="isForwardDelivery"
                    @input="onBuyPriceInput"
                  />
                  <div class="invalid-feedback">
                    Buy price must be greater than 0
                  </div>
                  <div
                    v-if="forwardDealPath"
                    class="form-text"
                    data-testid="buy-price-deal-hint"
                  >
                    Producer prices are set on the
                    <router-link :to="forwardDealPath">deal page</router-link>.
                  </div>
                  <div
                    v-else-if="isSwap"
                    class="form-text text-warning"
                    data-testid="swap-marker"
                  >
                    Swap: margin {{ (unitPrice - buyPrice).toFixed(4) }}
                    {{ currency }}/MWh
                  </div>
                </div>
                <div
                  v-if="isForwardDelivery"
                  class="col-md-4 mb-3"
                  data-testid="delivery-trade-owner"
                >
                  <label class="form-label">Trade owner</label>
                  <p class="form-control-plaintext py-0 mb-0">
                    {{ deliveryTradeOwnerText }}
                  </p>
                  <div v-if="forwardDealPath" class="form-text">
                    Set in Producer prices on the
                    <router-link :to="forwardDealPath">deal page</router-link>.
                  </div>
                </div>
                <div v-else-if="canEditSwapReferrer" class="col-md-4 mb-3">
                  <label class="form-label">Trade owner</label>
                  <SearchableSelectComponent
                    :options="swapReferrerOptions"
                    v-model="swapReferrerId"
                    placeholder="Select trade owner..."
                  />
                  <div class="form-text">
                    Paid commission on the swap margin.
                  </div>
                </div>
              </div>
              <div class="mb-3">
                <label for="description" class="form-label"
                  >Description (e.g., vintage)</label
                >
                <input
                  type="text"
                  class="form-control"
                  id="description"
                  v-model="description"
                  :disabled="isForwardDelivery"
                />
              </div>
              <!-- A delivery's notes are the deal page's, shown in the header above -->
              <div v-if="!isForwardDelivery" class="mb-3">
                <label for="internalNotes" class="form-label"
                  >Internal notes</label
                >
                <textarea
                  class="form-control"
                  id="internalNotes"
                  v-model="internalNotes"
                  rows="3"
                  :maxlength="INTERNAL_NOTES_MAX"
                  data-testid="transaction-internal-notes"
                ></textarea>
                <small class="form-text text-muted">
                  Internal only, never shown to producers or buyers
                </small>
              </div>
              <div class="form-check mb-3">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="excludeReferralCommission"
                  v-model="excludeReferralCommission"
                />
                <label class="form-check-label" for="excludeReferralCommission">
                  Exclude referral commission
                </label>
                <small class="form-text text-muted d-block">
                  When checked, no referral bonus invoice will be generated for
                  this transaction
                </small>
              </div>
            </div>
            <div class="col-lg-6">
              <!-- Tender Sheet Links -->
              <div v-if="volumesSpecificationLinks" class="mb-3">
                <label for="volumesSpecificationLinks" class="form-label"
                  >Tender Sheet Links (comma-separated)</label
                >
                <div class="input-group">
                  <input
                    type="text"
                    class="form-control"
                    id="volumesSpecificationLinks"
                    v-model="volumesSpecificationLinks"
                    placeholder="Generated tender sheet links will appear here"
                    readonly
                  />
                  <button
                    v-if="volumesSpecificationLinks"
                    class="btn btn-sm btn-outline-secondary"
                    type="button"
                    @click="openLinks(volumesSpecificationLinks)"
                    title="Open all links in new tabs"
                  >
                    <font-awesome-icon icon="external-link-alt" />
                  </button>
                </div>
                <small v-if="volumesSpecificationLinks" class="text-muted">
                  {{ getDocumentLinks(volumesSpecificationLinks).length }}
                  sheet(s) generated
                </small>
              </div>

              <div class="mb-3">
                <label for="contractLinks" class="form-label"
                  >Contract Links (comma-separated)</label
                >
                <div class="input-group">
                  <textarea
                    class="form-control"
                    id="contractLinks"
                    v-model="contractLinks"
                    rows="2"
                    :disabled="isForwardDelivery"
                    placeholder="https://drive.google.com/..."
                  ></textarea>
                  <button
                    v-if="contractLinks"
                    class="btn btn-sm btn-outline-secondary"
                    type="button"
                    @click="openLinks(contractLinks)"
                    title="Open all links in new tabs"
                  >
                    <font-awesome-icon icon="external-link-alt" />
                  </button>
                </div>
                <small v-if="contractLinks" class="text-muted">
                  {{ getDocumentLinks(contractLinks).length }} link(s)
                </small>
              </div>

              <div class="mb-3">
                <label for="transactionLink" class="form-label"
                  >Transaction Folder Link
                  <a
                    href="https://drive.google.com/drive/u/0/folders/1vIStNhb0FgNpIQTz5RhTSroaodOOtuj0"
                    target="_blank"
                    >please link specific folder</a
                  ></label
                >
                <div class="input-group">
                  <input
                    type="text"
                    class="form-control"
                    id="transactionLink"
                    v-model="transactionLink"
                    :disabled="isForwardDelivery"
                    placeholder="https://drive.google.com/..."
                  />
                  <button
                    v-if="transactionLink"
                    class="btn btn-sm btn-outline-secondary"
                    type="button"
                    @click="openLinks(transactionLink)"
                    title="Open link in new tab"
                  >
                    <font-awesome-icon icon="external-link-alt" />
                  </button>
                </div>
              </div>

              <div class="mb-3">
                <label for="registrationAuthority" class="form-label"
                  >Registration Authority</label
                >
                <input
                  type="text"
                  class="form-control"
                  id="registrationAuthority"
                  :value="registrationAuthorityLabel"
                  placeholder="Derived from selected certificates"
                  readonly
                />
                <div v-if="hasMixedAuthorities" class="text-danger small mt-1">
                  Multiple authorities selected, to create a new transaction
                  select only single authority certificates
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Price Overrides -->
      <PriceOverridesPanel
        v-if="!isForwardDelivery"
        :entries="priceOverrideEntries"
        v-model="itemPrices"
        :default-price="unitPrice"
        :currency
        :default-open="isEditMode"
        @update:model-value="onItemPriceInput"
      />

      <!-- Create Mode: Portfolio Items Selection -->
      <div v-if="!isEditMode">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h3 class="mb-0">Select Portfolio Items</h3>
          <div class="d-flex align-items-center gap-2">
            <button
              type="button"
              class="btn btn-outline-success"
              @click="generateTenderSheet"
              :disabled="isGeneratingSheet || !hasSelection"
            >
              <span
                v-if="isGeneratingSheet"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              {{
                isGeneratingSheet ? "Generating..." : "Generate Tender Sheet"
              }}
            </button>
            <span
              :title="
                hasMinPriceViolation ? MIN_PRICE_VIOLATION_MESSAGE : undefined
              "
            >
              <button
                @click="saveTransaction"
                class="btn btn-primary"
                data-testid="save-transaction-btn"
                :disabled="
                  isSavingTransaction ||
                  hasMinPriceViolation ||
                  !hasSelection ||
                  !isValidBuyPrice ||
                  registrationAuthority === null
                "
              >
                <span
                  v-if="isSavingTransaction"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                {{
                  isSavingTransaction
                    ? isEditMode
                      ? "Updating..."
                      : "Creating..."
                    : isEditMode
                      ? "Update Transaction"
                      : "Create Transaction"
                }}
              </button>
            </span>
          </div>
        </div>

        <div
          v-if="hasMinPriceViolation"
          class="alert alert-danger py-2"
          data-testid="save-blocking-reason"
        >
          {{ MIN_PRICE_VIOLATION_MESSAGE }}
        </div>

        <InventorySelectionPanel
          :items="tableItems"
          :selection
          :loading="isLoadingItems"
          :table-options="createTableOptions"
          :auto-select-eligible="autoSelectEligible"
          v-model:spread="spreadOverCompanies"
          spread-toggle
        >
          <template #breakdowns="{ items, getMwh, total }">
            <SaleBreakdownExtrasComponent
              :items
              :get-mwh
              :total
              @view-details="showRegionDetailsModal"
            />
          </template>
        </InventorySelectionPanel>
      </div>

      <!-- Edit Mode: Country Breakdown -->
      <div v-else-if="isEditMode">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h3 class="mb-0">Transaction Volumes</h3>
          <div class="d-flex gap-2">
            <button
              v-if="!isForwardDelivery"
              type="button"
              class="btn btn-outline-success"
              data-testid="generate-tender-sheet-btn"
              @click="generateTenderSheet"
              :disabled="isGeneratingSheet"
            >
              <span
                v-if="isGeneratingSheet"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              {{
                isGeneratingSheet ? "Generating..." : "Generate Tender Sheet"
              }}
            </button>
            <button
              class="btn btn-sm btn-outline-danger"
              @click="confirmDeleteTransaction"
              :disabled="!canDeleteTransaction"
              v-if="canDeleteTransaction"
            >
              <font-awesome-icon icon="trash" /> Delete Transaction
            </button>
            <button
              @click="saveTransaction"
              class="btn btn-primary"
              data-testid="save-transaction-btn"
              :disabled="isSavingTransaction"
            >
              <span
                v-if="isSavingTransaction"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              {{ isSavingTransaction ? "Updating..." : "Update Transaction" }}
            </button>
          </div>
        </div>

        <LoadingIndicatorComponent v-if="isLoadingCountryBreakdown" />

        <div
          v-else-if="
            !issuingRegistryBreakdown ||
            Object.keys(issuingRegistryBreakdown).length === 0
          "
        >
          <div class="alert alert-info">
            No volumes found for this transaction.
          </div>
        </div>

        <InventorySelectionPanel
          v-else
          :items="selectedTableItems"
          :selection
          :table-options="editTableOptions"
          readonly
        >
          <template #breakdowns="{ items, getMwh, total }">
            <SaleBreakdownExtrasComponent
              :items
              :get-mwh
              :total
              :region-breakdown="issuingRegistryBreakdown"
              @view-details="showRegionDetailsModal"
            />
          </template>
        </InventorySelectionPanel>
      </div>
    </div>

    <!-- Breakdown Details Modal -->
    <BreakdownDetailsModal
      ref="breakdownDetailsModal"
      :title="modalTitle"
      :mode="modalMode"
      :companies="modalCompanies"
      :portfolioItems="modalPortfolioItems"
      :companyPortfolioItemsMap="modalCompanyPortfolioItemsMap"
    />
  </div>
</template>

<script setup lang="ts">
import CounterpartyPicker from "@/components/common/CounterpartyPicker.vue";
import { useAccount } from "@/composables/account/useAccount";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import ForwardsTransactionHeaderComponent from "@/forwards2/components/ForwardsTransactionHeaderComponent.vue";
import { dealRoute } from "@/forwards2/deals/dealRoute";
import { readsForwardsAsStaff } from "@/forwards2/forwardsGuard";
import NewSaleLinksComponent from "@/forwards2/picker/NewSaleLinksComponent.vue";
import { INTERNAL_NOTES_MAX } from "@/forwards2/picker/pickerForm";
import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  ALL_COUNTRIES,
  INFORMATIONAL_PORTFOLIO_TYPES,
  SELLABLE_PORTFOLIO_TYPES,
} from "@/constants";
import { ADMIN_TRANSACTION_COLUMNS } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import InventorySelectionPanel from "@/inventory_selection/InventorySelectionPanel.vue";
import type { InventoryTableOptions } from "@/inventory_selection/inventorySelectionTypes";
import { allItemsSelectable } from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import type {
  FlattenedPortfolioItem,
  PortfolioItemTransfer,
  SalesTransaction,
  SalesTransactionItem,
} from "@/types";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";

import BreakdownDetailsModal from "./components/BreakdownDetailsModal.vue";
import PriceOverridesPanel from "./components/PriceOverridesPanel.vue";
import type { RegistrationAuthority } from "@/utils/registryAccounts";
import { useCounterpartyForm } from "@/components/common/useCounterpartyForm";
import SaleBreakdownExtrasComponent, {
  type RegionBreakdownEntry,
} from "./components/SaleBreakdownExtrasComponent.vue";
import {
  buildItemPricesFromBuyPrice,
  buyPriceToItemPrice,
  hasItemPriceOverride,
  hydrateBuyPrice,
  shouldApplyBuyPriceToAllItems,
  isValidBuyPrice as isValidBuyPriceCheck,
} from "./buyPriceHelpers";
import {
  buildDeliveryPayload,
  isBlank,
  type DeliveryUpdatePayload,
  type SharedTransactionFields,
} from "./forwardDeliveryPayload";
import {
  DEFAULT_TRANSACTIONS_VIEW,
  transactionsListRoute,
  transactionView,
} from "./transactionsListViewHelpers";
import { useSwapReferrers } from "@/composables/useSwapReferrers";
import {
  type Currency,
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
} from "@/money/currency_constants";
import { openableLinks } from "@/utils/urlUtils";

const route = useRoute();
const router = useRouter();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const portfolioItemsStore = usePortfolioItemsStore();
const selection = usePortfolioSelection({
  policy: allItemsSelectable,
});
// Staff fill auto-select greedily until they choose to spread it over companies.
const spreadOverCompanies = ref(false);
const counterpartyPicker = ref<InstanceType<typeof CounterpartyPicker> | null>(
  null
);

// Detect edit vs create mode
const transactionId = computed(() => {
  const id = route?.params?.id;
  return id ? Number(id) : null;
});
const isEditMode = computed(() => transactionId.value !== null);
const detailsPanelOpen = ref(true);

// --- Forwards v2 ---
//
// A transaction that belongs to a forwards v2 delivery shows the deal's header and locks the fields
// the deal owns — it mirrors them back over anything typed here (`sync_metadata_to_transactions`).
// Selling future inventory is a deal, not a transaction, and lives on its own page; create mode
// links to it for staff — forwards staff, who alone cut deals (user decision, 2026-09-17), and a
// sales admin, who opens the deal pages read-only (2026-09-28) — and for nobody before the account
// resolves.
const { accountData, loaded } = useAccount();

const offersDealLinks = computed(
  () => !isEditMode.value && loaded.value && readsForwardsAsStaff(accountData)
);

// Escrow (FORWARD) rows are sold from the deal page, and the backend refuses them here.
const spotFormPortfolioTypes = [
  ...SELLABLE_PORTFOLIO_TYPES,
  ...INFORMATIONAL_PORTFOLIO_TYPES,
];
const unselectablePortfolioTypes = new Set([
  ...INFORMATIONAL_PORTFOLIO_TYPES,
  "FORWARD",
]);

// Current transaction data (for edit mode)
const currentTransaction = ref<SalesTransaction | null>(null);

const forwardDelivery = computed(
  () => currentTransaction.value?.forward_delivery ?? null
);

const isForwardDelivery = computed(() => forwardDelivery.value !== null);

// The backend says when a saved deal's currency is fixed; a delivery's is always its deal's.
const isCurrencyFixed = computed(
  () => currentTransaction.value?.currency_editable === false
);

// The list page shows spot sales and forward deliveries separately; return to the view this
// transaction belongs to (a new one is a spot sale).
const listRoute = computed(() =>
  transactionsListRoute(
    currentTransaction.value
      ? transactionView(currentTransaction.value)
      : DEFAULT_TRANSACTIONS_VIEW
  )
);

const forwardDealPath = computed(() =>
  forwardDelivery.value
    ? dealRoute(
        forwardDelivery.value.deal_id,
        forwardDelivery.value.delivery_id
      )
    : null
);

// Buyer details fields
const {
  counterpartyName,
  counterpartyCountry,
  counterpartyEecsAccountNumber: counterparty_eecs_account_number,
  counterpartyEmails,
  registrationAuthority,
  counterpartyModel,
  selectedGlobalCompany,
  handleCompanySelected,
  hydrateLinkedCompany,
} = useCounterpartyForm();
const registryUnitPricePln = ref<number | null>(null);
const transactionDate = ref("");
const unitPrice = ref(0);
const currency = ref<Currency>(DEFAULT_CURRENCY);
// A producer's minimum price is an EUR floor, so only an EUR deal's price can be checked against it.
const isEurDeal = computed(() => currency.value === DEFAULT_CURRENCY);
const description = ref("");
const internalNotes = ref("");
const excludeReferralCommission = ref(false);

// Contract fields
const contractLinks = ref("");
const transactionLink = ref("");
const volumesSpecificationLinks = ref("");

const clampedUnitPrice = computed(() => Math.max(0, unitPrice.value));

// Per-item price overrides (item_id → price or null for default)
const itemPrices = ref<Record<number, number | null>>({});

const buyPrice = ref(0);
// Until the buy price is edited by hand it just mirrors the sell price;
// once touched it keeps its own value (that divergence is what makes a swap).
const buyPriceEdited = ref(false);
// Whether the loaded transaction hydrated as a swap (uniform per-item buy
// price). Together with lastPriceEdit this is the explicit swap intent that
// gates stamping the buy price onto every item at save time.
const hydratedAsSwap = ref(false);
// The last price input the user touched — it wins at save time: a buy-price
// edit stamps every item, a per-item edit is sent as entered.
const lastPriceEdit = ref<"buy" | "items" | null>(null);
watch(unitPrice, (newPrice) => {
  if (buyPriceEdited.value || hydratedAsSwap.value) return;
  buyPrice.value = newPrice;
});

// Mirror a hand-edited buy price into the per-item overrides right away so the
// Price Overrides panel always shows what saving will persist. nextTick because
// v-model.number and this handler observe the same input event.
const onBuyPriceInput = () => {
  buyPriceEdited.value = true;
  lastPriceEdit.value = "buy";
  void nextTick(() => {
    if (typeof buyPrice.value !== "number" || buyPrice.value <= 0) return;
    const keys = priceOverrideEntries.value.map((e) => e.key);
    itemPrices.value = buildItemPricesFromBuyPrice(
      keys,
      buyPriceToItemPrice(buyPrice.value, unitPrice.value)
    );
  });
};
const isValidBuyPrice = computed(() =>
  isValidBuyPriceCheck(buyPrice.value, unitPrice.value)
);
const isSwap = computed(
  () => buyPrice.value > 0 && buyPrice.value !== unitPrice.value
);
// Overrides restricted to items/companies still present in the overrides
// table — deselecting a company leaves a stale itemPrices key behind that
// must not count as an active override. The payload applies the same filter.
const effectiveItemPrices = computed((): Record<number, number | null> => {
  const validKeys = new Set(priceOverrideEntries.value.map((e) => e.key));
  return Object.fromEntries(
    Object.entries(itemPrices.value).filter(([key]) =>
      validKeys.has(Number(key))
    )
  );
});
// The backend pays the swap referrer whenever any item price diverges from
// the sell price, so the trade owner must be editable in that state too —
// not only when the top-level buy price diverges.
const canEditSwapReferrer = computed(
  () => isSwap.value || hasItemPriceOverride(effectiveItemPrices.value)
);
// A per-item edit that clears the last override dissolves the swap: reset the
// buy price so a stale hydrated value can't keep isSwap (and with it the
// referrer) alive. nextTick because the v-model assignment and this handler
// observe the same update event.
const onItemPriceInput = () => {
  lastPriceEdit.value = "items";
  void nextTick(() => {
    if (!hasItemPriceOverride(effectiveItemPrices.value)) {
      buyPrice.value = unitPrice.value;
      hydratedAsSwap.value = false;
    }
  });
};

const { options: swapReferrerOptions, fetchCandidates: fetchSwapReferrers } =
  useSwapReferrers();
const swapReferrerId = ref<number | null>(null);
// A trade owner that has lost its commission config is no longer a candidate, but is still shown.
const deliveryTradeOwnerText = computed(() => {
  const id = currentTransaction.value?.swap_referrer_company_id ?? null;
  if (id === null) return "None";
  return (
    swapReferrerOptions.value.find((option) => option.value === id)?.label ??
    `#${id}`
  );
});

// Unified list for the price overrides table.
// In edit mode: entries from saved transaction items (keyed by item ID).
// In create mode: entries from selected portfolio items grouped by company (keyed by company ID).
const priceOverrideEntries = computed(
  (): { key: number; company_name: string; amount: number }[] => {
    if (isEditMode.value) {
      if (!currentTransaction.value?.items) return [];
      return Object.values(currentTransaction.value.items)
        .map((item) => ({
          key: item.id,
          company_name: item.company_name,
          amount: item.amount,
        }))
        .sort((a, b) => a.company_name.localeCompare(b.company_name));
    }

    const groups = new Map<number, { company_name: string; amount: number }>();
    for (const item of selection.selectedItems) {
      const amount = selection.getSelectedCount(item.id) ?? 0;
      const existing = groups.get(item.company_id);
      if (existing) {
        existing.amount += amount;
      } else {
        groups.set(item.company_id, {
          company_name: item.company_name,
          amount,
        });
      }
    }
    return Array.from(groups.entries())
      .map(([companyId, data]) => ({
        key: companyId,
        company_name: data.company_name,
        amount: data.amount,
      }))
      .sort((a, b) => a.company_name.localeCompare(b.company_name));
  }
);

// Auto-select skips rows the sale cannot include: informational portfolios, minimum-price rows
// on a non-EUR deal, and on an EUR deal minimum-price rows whose floor the sell price does not
// clear. A new function per price re-runs auto-select, so the price is read here in the getter (a
// read inside the returned closure would not be tracked).
const autoSelectEligible = computed(() => {
  const price = unitPrice.value;
  const excludesLimitPrice = !isEurDeal.value;
  return (item: FlattenedPortfolioItem): boolean =>
    !INFORMATIONAL_PORTFOLIO_TYPES.has(item.portfolio_type) &&
    !(item.portfolio_type === "SPOT_MIN_PRICE" && excludesLimitPrice) &&
    !(
      item.portfolio_type === "SPOT_MIN_PRICE" &&
      item.limit_price &&
      price > 0 &&
      parseFloat(item.limit_price) > price
    );
});

// A minimum-price row's floor is an EUR figure, so the backend refuses it in a non-EUR deal.
const createUnselectablePortfolioTypes = computed(() =>
  isEurDeal.value
    ? unselectablePortfolioTypes
    : new Set([...unselectablePortfolioTypes, "SPOT_MIN_PRICE"])
);

const createTableOptions = computed((): InventoryTableOptions => ({
  storageContext: "admin-transaction",
  defaultVisibleColumns: ADMIN_TRANSACTION_COLUMNS,
  isForAdmin: true,
  unselectablePortfolioTypes: createUnselectablePortfolioTypes.value,
  defaultPortfolioTypeFilter: SELLABLE_PORTFOLIO_TYPES,
}));

// Switching a deal away from EUR drops the minimum-price rows it can no longer include. Edit mode's
// selection is the saved allocation, which this form does not change.
watch(isEurDeal, (eurDeal) => {
  if (eurDeal || isEditMode.value) return;
  selection.setSelectedItems(
    selection.selectedItems.filter(
      (item) => item.portfolio_type !== "SPOT_MIN_PRICE"
    )
  );
});

// Edit mode's selection is the saved allocation, which this form does not change: a draft holding
// minimum-price rows stays in EUR, where create mode would drop the rows instead.
const allocationHoldsLimitPriceRows = computed(
  () =>
    isEditMode.value &&
    selection.selectedItems.some(
      (item) => item.portfolio_type === "SPOT_MIN_PRICE"
    )
);

const editTableOptions: InventoryTableOptions = {
  storageContext: "admin-transaction-edit",
  defaultVisibleColumns: ADMIN_TRANSACTION_COLUMNS,
  isForAdmin: true,
};

// Loading state
const isLoadingItems = ref(false);
const isSavingTransaction = ref(false);
const isGeneratingSheet = ref(false);
const isLoadingCountryBreakdown = ref(false);
const isDeletingTransaction = ref(false);

// Edit mode state
const issuingRegistryBreakdown = ref<Record<string, any>>({});

const regionAuthorityMapping = ref<Record<string, RegistrationAuthority>>({});

// Modal management
const breakdownDetailsModal = ref<InstanceType<
  typeof BreakdownDetailsModal
> | null>(null);

// --- Data Loading ---

const fetchAdminPortfolioItems = async () => {
  isLoadingItems.value = true;
  try {
    const params = new URLSearchParams();
    for (const pt of spotFormPortfolioTypes) {
      params.append("portfolio_types[]", pt);
    }
    const response = await goSolidApi.get(
      `/api/admin/portfolios/flattened-portfolio-items?${params.toString()}`
    );
    portfolioItemsStore.setAllItems(response.data.portfolio_items ?? []);
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isLoadingItems.value = false;
  }
};

const loadRegionAuthorityMapping = async () => {
  try {
    const response = await goSolidApi.get(
      "/api/admin/transactions/country-registry-mapping"
    );
    regionAuthorityMapping.value = Object.fromEntries(
      Object.entries(
        response.data as Record<
          string,
          { registration_authority: RegistrationAuthority }
        >
      ).map(([region, info]) => [region, info.registration_authority])
    );
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

portfolioItemsStore.registerRefetch(fetchAdminPortfolioItems);

watch(transactionDate, (newDate) => {
  if (isEditMode.value || !newDate) return;
  description.value = `EAC sale(Price locked on ${newDate})`;
});

onUnmounted(() => {
  selection.clearSelection();
});

// --- Computed Summaries ---

const totalSelectedMwh = computed(() => selection.totalSelectedMwh);

const hasSelection = computed(() => selection.selectedItemIds.size > 0);

const withAuthority = (items: FlattenedPortfolioItem[]) =>
  items.map((item) => ({
    ...item,
    registration_authority:
      authorityForRegion(item.registry_region) ?? undefined,
  }));

const tableItems = computed(() => withAuthority(portfolioItemsStore.allItems));

const selectedTableItems = computed(() =>
  withAuthority(selection.selectedItems)
);

// NOTE: null (not an AIB fallback) for unmapped or not-yet-loaded regions —
// the result is saved as the transaction's authority, so a silent default
// would mislabel non-AIB certificates; null keeps saving blocked instead.
function authorityForRegion(
  region: string | null | undefined
): RegistrationAuthority | null {
  return (region && regionAuthorityMapping.value[region]) || null;
}

const selectedAuthorities = computed(
  () =>
    new Set(
      selection.selectedItems.map((item) =>
        authorityForRegion(item.registry_region)
      )
    )
);

const hasMixedAuthorities = computed(() => selectedAuthorities.value.size > 1);

const REGISTRATION_AUTHORITY_LABELS: Record<RegistrationAuthority, string> = {
  AIB: "AIB (EECS)",
  REGO: "REGO (Ofgem)",
  RGP: "RGP (Polish GO / TGE)",
};

const registrationAuthorityLabel = computed(() =>
  registrationAuthority.value
    ? REGISTRATION_AUTHORITY_LABELS[registrationAuthority.value]
    : ""
);

// NOTE: gated on create mode — in edit mode the stored authority is
// authoritative and the selection is not user-editable.
watch(
  selectedAuthorities,
  (authorities) => {
    if (isEditMode.value) return;
    registrationAuthority.value =
      authorities.size === 1 ? [...authorities][0] : null;
  },
  { immediate: true }
);

// NOTE: the RGP price input is destroyed by v-if on authority switch, so a
// badInput flag left behind would keep blocking save with no visible field.
watch(registrationAuthority, () => {
  registryUnitPriceBadInput.value = false;
});

const MIN_PRICE_VIOLATION_MESSAGE =
  "Sell price is below the minimum price of some selected certificates";

const hasMinPriceViolation = computed(() => {
  if (!isEurDeal.value || !hasSelection.value || unitPrice.value <= 0)
    return false;
  return selection.selectedItems.some(
    (item) =>
      item.portfolio_type === "SPOT_MIN_PRICE" &&
      item.limit_price &&
      parseFloat(item.limit_price) > unitPrice.value
  );
});

const excludesLimitPriceRows = computed(
  () =>
    !isEurDeal.value &&
    tableItems.value.some((item) => item.portfolio_type === "SPOT_MIN_PRICE")
);

// NOTE: characters the number input accepts but cannot parse (e.g. "eee..") surface
// as an empty v-model value, indistinguishable from a cleared field — only the DOM
// element's validity.badInput reveals them.
const registryUnitPriceBadInput = ref(false);
const onRegistryUnitPriceInput = (event: Event) => {
  registryUnitPriceBadInput.value = (
    event.target as HTMLInputElement
  ).validity.badInput;
};

const isValidRegistryUnitPrice = computed(() => {
  if (registrationAuthority.value !== "RGP") return true;
  if (registryUnitPriceBadInput.value) return false;
  const price = registryUnitPricePln.value;
  if (price === null || (price as unknown) === "") {
    return true;
  }
  if (typeof price !== "number" || !Number.isFinite(price) || price <= 0) {
    return false;
  }
  const cents = price * 100;
  return Math.abs(cents - Math.round(cents)) < 1e-9;
});

const canDeleteTransaction = computed(() => {
  if (!currentTransaction.value) return false;
  // A forwards v2 delivery is voided through its deal: the legacy delete detaches the delivery's
  // certificates and merges the fragments back, which drops the rows its escrow points at. The
  // backend refuses it, so the button could only ever produce an error (C-FIX4-01).
  if (isForwardDelivery.value) return false;
  return !["DELIVERED", "COMPLETED"].includes(currentTransaction.value.status);
});

const transactionTotalMwh = computed(() => {
  if (!issuingRegistryBreakdown.value) return 0;
  return Object.values(issuingRegistryBreakdown.value).reduce(
    (total: number, breakdown: any) => total + breakdown.total_mwh,
    0
  );
});

// --- Field Mappings (for edit mode load/save) ---

/** The FIELD_MAPPINGS columns as a save sends them; a blank input's key is left out. */
interface MappedTransactionFields {
  counterparty_name: string;
  counterparty_country: string;
  counterparty_eecs_account_number: string;
  counterparty_emails: string;
  transaction_at: string;
  unit_price: string;
  currency: Currency;
  description: string;
  contract_links: string;
  transaction_link: string;
  volumes_specification_links: string;
  exclude_referral_commission: boolean;
}

/** The body of a spot transaction's save: the PUT of an edit, and the POST without its items. */
interface TransactionSavePayload
  extends Partial<MappedTransactionFields>, SharedTransactionFields {
  global_company_id: number | null;
  internal_notes: string;
  item_prices?: Record<number, number | null>;
}

interface TransactionCreatePayload extends TransactionSavePayload {
  portfolio_items: PortfolioItemTransfer[];
}

interface FieldConfig {
  ref: any;
  apiKey: string;
  toApi?: (value: any) => any;
  fromApi?: (value: any) => any;
}

const FIELD_MAPPINGS: Record<keyof MappedTransactionFields, FieldConfig> = {
  counterparty_name: { ref: counterpartyName, apiKey: "counterparty_name" },
  counterparty_country: {
    ref: counterpartyCountry,
    apiKey: "counterparty_country",
  },
  counterparty_eecs_account_number: {
    ref: counterparty_eecs_account_number,
    apiKey: "counterparty_eecs_account_number",
  },
  counterparty_emails: {
    ref: counterpartyEmails,
    apiKey: "counterparty_emails",
  },
  transaction_at: {
    ref: transactionDate,
    apiKey: "transaction_at",
    fromApi: (val: string) => val.split("T")[0],
  },
  unit_price: {
    ref: unitPrice,
    apiKey: "unit_price",
    // Sent as typed, never rounded: the API refuses a price finer than the column's 4 decimals instead
    // of the form quietly saving a different price than the contract's.
    toApi: () => String(clampedUnitPrice.value),
    fromApi: (val: string) => Number(val),
  },
  // Sent on every save: the backend accepts an unchanged currency even once it is fixed.
  currency: { ref: currency, apiKey: "currency" },
  description: { ref: description, apiKey: "description" },
  contract_links: { ref: contractLinks, apiKey: "contract_links" },
  transaction_link: { ref: transactionLink, apiKey: "transaction_link" },
  volumes_specification_links: {
    ref: volumesSpecificationLinks,
    apiKey: "volumes_specification_links",
  },
  exclude_referral_commission: {
    ref: excludeReferralCommission,
    apiKey: "exclude_referral_commission",
  },
};

const buildTransactionPayload = (
  authority: RegistrationAuthority
): TransactionSavePayload => {
  // FieldConfig.ref is untyped, so this half of the body is asserted rather than inferred.
  const mappedFields = Object.fromEntries(
    Object.values(FIELD_MAPPINGS)
      .filter((config) => !isBlank(config.ref.value))
      .map((config) => [
        config.apiKey,
        config.toApi ? config.toApi(config.ref.value) : config.ref.value,
      ])
  ) as Partial<MappedTransactionFields>;

  const payload: TransactionSavePayload = {
    ...mappedFields,
    // Send an explicit null so unlinking persists; the generic FIELD_MAPPINGS path
    // drops nulls (PATCH-style "field omitted means no change") and would otherwise
    // skip the unlink.
    global_company_id: selectedGlobalCompany.value?.id ?? null,
    // Always sent, so clearing the textarea clears the notes; the backend stores a blank as null.
    internal_notes: internalNotes.value,
    ...sharedTransactionFields(authority),
  };

  if (
    shouldApplyBuyPriceToAllItems({
      isSwap: isSwap.value,
      lastPriceEdit: lastPriceEdit.value,
      hydratedAsSwap: hydratedAsSwap.value,
    })
  ) {
    // Swap deal: the buy price is the producer's per-item price for every item. Derive the
    // overrides at save time (rather than only on the buy-input event) so the buy price always
    // reaches the current selection regardless of input ordering.
    const keys = priceOverrideEntries.value.map((e) => e.key);
    payload.item_prices = buildItemPricesFromBuyPrice(keys, buyPrice.value);
  } else {
    // Send the per-item overrides as shown, filtered to keys that still exist
    // in priceOverrideEntries (avoids sending stale keys for deselected companies/items).
    const filteredPrices = effectiveItemPrices.value;
    if (Object.keys(filteredPrices).length > 0) {
      payload.item_prices = filteredPrices;
    }
  }

  return payload;
};

const buildForwardDeliveryPayload = (
  authority: RegistrationAuthority
): DeliveryUpdatePayload =>
  buildDeliveryPayload(
    {
      counterpartyEecsAccountNumber: counterparty_eecs_account_number.value,
      excludeReferralCommission: excludeReferralCommission.value,
      tradeOwnerCompanyId:
        currentTransaction.value?.swap_referrer_company_id ?? null,
    },
    sharedTransactionFields(authority)
  );

const sharedTransactionFields = (
  authority: RegistrationAuthority
): SharedTransactionFields => ({
  // Read-only in edit mode, so it always echoes what was loaded — but it has to travel, because
  // it is the key the backend validates registration_authority_data against.
  counterparty_registration_authority: authority,
  registration_authority_data:
    authority === "RGP" && typeof registryUnitPricePln.value === "number"
      ? { unit_price_pln: registryUnitPricePln.value.toFixed(2) }
      : null,
  // The swap referrer travels with swap-ness itself (any item price override
  // counts), independent of which price input drives the item prices.
  swap_referrer_company_id: canEditSwapReferrer.value
    ? swapReferrerId.value
    : null,
});

// --- Save Logic ---

const saveTransaction = async () => {
  const authority = registrationAuthority.value;
  // touchAndValidate covers the authority-specific account check via the
  // accountValidation prop.
  if (
    !counterpartyPicker.value?.touchAndValidate() ||
    !isValidRegistryUnitPrice.value ||
    !isValidBuyPrice.value ||
    authority === null
  ) {
    globalErrorHandler("Please fix validation errors before saving");
    return;
  }

  try {
    isSavingTransaction.value = true;

    if (isEditMode.value) {
      // A delivery's per-producer prices are its deal's: leaving `item_prices` out tells the
      // backend "no change", where a stamped buy price would rewrite every line, zero-amount
      // ones included.
      const payload = isForwardDelivery.value
        ? buildForwardDeliveryPayload(authority)
        : buildTransactionPayload(authority);
      const response = await goSolidApi.put(
        `/api/admin/transactions/${transactionId.value}`,
        payload
      );

      if (response.status !== 200) {
        throw new Error("Error updating transaction.");
      }

      await fetchTransaction();

      const statusMessage = currentTransaction.value?.status
        ? `Status: ${currentTransaction.value.status}`
        : "";

      globalSuccessHandler(
        "Transaction updated",
        `Transaction saved successfully. ${statusMessage}`
      );
    } else {
      const payload: TransactionCreatePayload = {
        ...buildTransactionPayload(authority),
        portfolio_items: selection.selectedItemsForApi,
      };

      const response = await goSolidApi.post(
        "/api/admin/transactions",
        payload
      );

      if (response.status !== 201) {
        throw new Error("Error creating transaction.");
      }

      globalSuccessHandler(
        "Transaction created",
        "Transaction created successfully. You can now edit it to add buyer details."
      );

      router.push(listRoute.value);
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isSavingTransaction.value = false;
  }
};

// --- Edit Mode Fetching ---

const fetchTransaction = async () => {
  if (!transactionId.value) return;

  try {
    const response = await goSolidApi.get(
      `/api/admin/transactions/${transactionId.value}`
    );
    if (response.status === 200 && response.data) {
      const transaction = response.data.transaction;
      currentTransaction.value = transaction;

      issuingRegistryBreakdown.value =
        response.data.issuing_registry_breakdown || {};

      // Hydrate the shared cache and selection from the saved allocation so
      // edit-mode reads (totals, breakdowns, table rendering) go through the
      // same source of truth as create mode.
      if (response.data.portfolio_items) {
        const apiItems = response.data
          .portfolio_items as (FlattenedPortfolioItem & {
          selected_count: number;
        })[];
        const cacheItems: FlattenedPortfolioItem[] = apiItems.map(
          ({ selected_count: _ignored, ...rest }) => rest
        );
        portfolioItemsStore.setAllItems(cacheItems);
        selection.clearSelection();
        for (const item of apiItems) {
          selection.updateItemSelectedCount(item.id, item.selected_count);
        }
      }

      Object.entries(FIELD_MAPPINGS).forEach(([_, config]) => {
        const value = transaction[config.apiKey];
        if (value !== null && value !== undefined) {
          config.ref.value = config.fromApi ? config.fromApi(value) : value;
        }
      });

      if (transaction.counterparty_registration_authority) {
        registrationAuthority.value =
          transaction.counterparty_registration_authority;
      }
      registryUnitPricePln.value = transaction.registration_authority_data
        ?.unit_price_pln
        ? parseFloat(transaction.registration_authority_data.unit_price_pln)
        : null;

      hydrateLinkedCompany(transaction);

      // Populate per-item price overrides from transaction items
      if (transaction.items) {
        const items = Object.values(
          transaction.items
        ) as SalesTransactionItem[];
        const prices: Record<number, number | null> = {};
        for (const item of items) {
          prices[item.id] = item.unit_price
            ? parseFloat(item.unit_price)
            : null;
        }
        itemPrices.value = prices;
        // A delivery's uniform per-producer price is the deal's, not a swap this page set; reading
        // it as one would stamp it over every line on the next save.
        const hydrated = isForwardDelivery.value
          ? { buyPrice: unitPrice.value, isSwap: false }
          : hydrateBuyPrice(
              items.map((item) => ({
                amount: item.amount,
                price: prices[item.id],
              })),
              unitPrice.value
            );
        buyPrice.value = hydrated.buyPrice;
        hydratedAsSwap.value = hydrated.isSwap;
        lastPriceEdit.value = null;
        buyPriceEdited.value = false;
      }
      swapReferrerId.value = transaction.swap_referrer_company_id ?? null;
      internalNotes.value = transaction.internal_notes ?? "";
    } else {
      throw new Error("Error fetching transaction.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

// --- Tender Sheet ---

interface TenderSheetLinks {
  spreadsheet_link: string;
  folder_link: string | null;
  /** Set when a saved transaction was sheeted: recording its folder can advance its status. */
  transaction_status?: SalesTransaction["status"] | null;
}

// Create mode sheets the unsaved selection; edit mode sheets the saved transaction's volumes and the
// backend records the links on it, so a sheet and folder can be produced after the sale is agreed.
const requestTenderSheet = async (): Promise<TenderSheetLinks | null> => {
  if (isEditMode.value) {
    const response = await goSolidApi.post<TenderSheetLinks>(
      `/api/admin/transactions/${transactionId.value}/tender-sheet`
    );
    return response.data;
  }
  const selectedForApi = selection.selectedItemsForApi;
  if (selectedForApi.length === 0) {
    globalErrorHandler("No portfolio items selected.");
    return null;
  }
  const response = await goSolidApi.post<TenderSheetLinks>(
    "/api/admin/generate-tender-sheet",
    { portfolio_items: selectedForApi }
  );
  return response.data;
};

const generateTenderSheet = async () => {
  try {
    isGeneratingSheet.value = true;
    const links = await requestTenderSheet();
    if (!links) return;

    window.open(links.spreadsheet_link, "_blank", "noopener,noreferrer");

    volumesSpecificationLinks.value = volumesSpecificationLinks.value
      ? `${volumesSpecificationLinks.value},${links.spreadsheet_link}`
      : links.spreadsheet_link;
    if (links.folder_link && !transactionLink.value) {
      transactionLink.value = links.folder_link;
    }
    // Only the status is taken from the backend: a refetch would drop unsaved edits in the form.
    if (links.transaction_status && currentTransaction.value) {
      currentTransaction.value = {
        ...currentTransaction.value,
        status: links.transaction_status,
      };
    }

    globalSuccessHandler(
      "Tender sheet generated",
      `Sheet generated with ${selection.selectedItems.length} portfolio items (${totalSelectedMwh.value} MWh)`
    );
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isGeneratingSheet.value = false;
  }
};

// --- Helpers ---

const getDocumentLinks = (linksString: string | null) => {
  if (!linksString) return [];
  return linksString.split(",").filter((link) => link.trim());
};

const openLinks = (linksString: string | null) => {
  openableLinks(linksString).forEach((link) => {
    window.open(link, "_blank", "noopener,noreferrer");
  });
};

const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case "DRAFT":
      return "bg-secondary";
    case "PENDING_BUYER_DETAILS":
      return "bg-info";
    case "PENDING_CONTRACTS":
      return "bg-warning text-dark";
    case "READY_TO_TRANSFER":
      return "bg-primary";
    case "DELIVERED":
      return "bg-info";
    case "COMPLETED":
      return "bg-success";
    default:
      return "bg-secondary";
  }
};

const confirmDeleteTransaction = async () => {
  if (!transactionId.value) return;

  const confirmed = confirm(
    `Are you sure you want to delete this transaction?\n\nThis will dissociate ${transactionTotalMwh.value} MWh and cannot be undone.`
  );

  if (!confirmed) return;

  try {
    isDeletingTransaction.value = true;
    const response = await goSolidApi.delete(
      `/api/admin/transactions/${transactionId.value}`
    );

    if (response.status === 200) {
      globalSuccessHandler(
        "Transaction deleted",
        "Transaction and all associations have been removed"
      );
      router.push(listRoute.value);
    } else {
      throw new Error("Error deleting transaction.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isDeletingTransaction.value = false;
  }
};

// Modal configuration
const modalTitle = ref("");
const modalMode = ref<"companies" | "certificates">("certificates");
const modalCompanies = ref<any[]>([]);
const modalPortfolioItems = ref<any[]>([]);
const modalCompanyPortfolioItemsMap = ref<Record<number, any[]>>({});

const showRegionDetailsModal = (regionData: RegionBreakdownEntry) => {
  modalTitle.value = `${regionData.region} Details`;
  modalMode.value = "companies";
  modalCompanies.value = regionData.companies;
  modalPortfolioItems.value = regionData.portfolio_items;

  const itemMap: Record<number, any[]> = {};
  modalPortfolioItems.value.forEach((item: any) => {
    if (!itemMap[item.company_id]) {
      itemMap[item.company_id] = [];
    }
    itemMap[item.company_id].push(item);
  });
  modalCompanyPortfolioItemsMap.value = itemMap;

  breakdownDetailsModal.value?.toggleModal();
};

// --- Lifecycle ---

onMounted(async () => {
  await fetchSwapReferrers();

  if (isEditMode.value) {
    await Promise.allSettled([
      fetchTransaction(),
      loadRegionAuthorityMapping(),
    ]);
  } else {
    selection.clearSelection();

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    description.value = `EAC sale(Price locked on ${formattedDate})`;

    await Promise.allSettled([
      fetchAdminPortfolioItems(),
      loadRegionAuthorityMapping(),
    ]);
  }
});

// --- Provide/Inject ---
// NOTE/WORKAROUND: We don't actually show the actions tab and thus don't need to navigate nowhere, hence the empty method
provide("navigateToInventoryTab", (_action: string) => {});
</script>

<style lang="scss" scoped>
#AdminTransactionsNew {
  flex-grow: 1;
  text-align: left;
  overflow-y: auto;
  max-width: 100%;
}
.container {
  max-width: 1400px;
}
</style>
